import React, { useEffect, useState, createElement } from 'react';
import { XIcon, CheckCircleIcon } from 'lucide-react';
import { useConsultationForm } from './ConsultationFormContext';
// Load gtag.js script once
function loadGtagScript() {
  if (document.getElementById('gtag-script')) return;
  const script = document.createElement('script');
  script.id = 'gtag-script';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-830901894';
  document.head.appendChild(script);
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag(...args: any[]) {
    w.dataLayer.push(args);
  }
  w.gtag = gtag;
  gtag('js', new Date());
  gtag('config', 'AW-830901894');
}
export function ConsultationFormModal() {
  const {
    showForm,
    closeForm
  } = useConsultationForm();
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    transactionalConsent: false,
    marketingConsent: false
  });
  // Load gtag script on mount
  useEffect(() => {
    loadGtagScript();
  }, []);
  // Fire conversion event when thank you screen shows
  useEffect(() => {
    if (submitted) {
      const w = window as any;
      if (typeof w.gtag === 'function') {
        w.gtag('event', 'conversion', {
          send_to: 'AW-830901894'
        });
      }
    }
  }, [submitted]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    window.open('https://calendly.com/be_clinical/', '_blank');
  };
  const handleClose = () => {
    setSubmitted(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      transactionalConsent: false,
      marketingConsent: false
    });
    closeForm();
  };
  if (!showForm) return null;
  return <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-id="element-29">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} data-id="element-30" />
      <div className="relative bg-white rounded-md shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-8" data-id="element-31">
        <button onClick={handleClose} className="absolute top-4 right-4 text-text-dark/40 hover:text-text-dark transition-colors" aria-label="Close form" data-id="element-32">
          <XIcon className="w-5 h-5" data-id="element-33" />
        </button>

        {submitted ? <div className="text-center py-8" data-id="element-34">
            <CheckCircleIcon className="w-16 h-16 text-gold mx-auto mb-4" data-id="element-35" />
            <h3 className="font-serif text-2xl text-text-dark mb-2" data-id="element-36">
              Thank You!
            </h3>
            <p className="text-text-dark/60 mb-2" data-id="element-37">
              Your consultation request has been received.
            </p>
            <p className="text-sm text-text-dark/50 mb-8" data-id="element-38">
              A scheduling page has opened in a new tab. If it didn't open,{' '}
              <a href="https://calendly.com/be_clinical/" target="_blank" rel="noopener noreferrer" className="text-gold underline" data-id="element-39">
                click here
              </a>
              .
            </p>
            <button onClick={handleClose} className="bg-gold hover:bg-gold/90 text-white font-medium px-8 py-3 rounded-sm tracking-wide uppercase text-sm transition-all duration-300" data-id="element-40">
              Close
            </button>
          </div> : <>
            <h3 className="font-serif text-2xl text-text-dark mb-1" data-id="element-41">
              Book Consultation
            </h3>
            <p className="text-sm text-text-dark/60 mb-1 font-medium flex items-center gap-1.5" data-id="element-42">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-43">
                <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" data-id="element-44" />
                <circle cx="12" cy="10" r="3" data-id="element-45" />
              </svg>
              Be Clinical Med Spa in San Ramon, CA
            </p>
            <p className="text-xs text-text-dark/45 mb-6 ml-5" data-id="element-46">
              Off of 680 Freeway and Crow Canyon Road
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-47">
              <div className="grid grid-cols-2 gap-4" data-id="element-48">
                <div data-id="element-49">
                  <label htmlFor="firstName" className="block text-xs font-medium text-text-dark/70 mb-1 uppercase tracking-wider" data-id="element-50">
                    First Name *
                  </label>
                  <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold transition-colors" data-id="element-51" />
                </div>
                <div data-id="element-52">
                  <label htmlFor="lastName" className="block text-xs font-medium text-text-dark/70 mb-1 uppercase tracking-wider" data-id="element-53">
                    Last Name *
                  </label>
                  <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold transition-colors" data-id="element-54" />
                </div>
              </div>

              <div data-id="element-55">
                <label htmlFor="email" className="block text-xs font-medium text-text-dark/70 mb-1 uppercase tracking-wider" data-id="element-56">
                  Email *
                </label>
                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold transition-colors" data-id="element-57" />
              </div>

              <div data-id="element-58">
                <label htmlFor="phone" className="block text-xs font-medium text-text-dark/70 mb-1 uppercase tracking-wider" data-id="element-59">
                  Phone Number *
                </label>
                <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} className="w-full border border-gray-200 rounded-sm px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold transition-colors" data-id="element-60" />
              </div>

              <div className="space-y-4 pt-2" data-id="element-61">
                <label className="flex items-start gap-3 cursor-pointer" data-id="element-62">
                  <input type="checkbox" name="transactionalConsent" required checked={formData.transactionalConsent} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold/30 shrink-0" data-id="element-63" />
                  <span className="text-xs text-text-dark/60 leading-relaxed" data-id="element-64">
                    By checking this box, I consent to receive non-marketing
                    text messages from Be Clinical about appointment
                    confirmations, service updates, and scheduling. Message
                    frequency varies. Message &amp; data rates may apply. Reply
                    STOP to unsubscribe at any time. *
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer" data-id="element-65">
                  <input type="checkbox" name="marketingConsent" checked={formData.marketingConsent} onChange={handleChange} className="mt-1 w-4 h-4 rounded border-gray-300 text-gold focus:ring-gold/30 shrink-0" data-id="element-66" />
                  <span className="text-xs text-text-dark/60 leading-relaxed" data-id="element-67">
                    By checking this box, I consent to receive marketing text
                    messages from Be Clinical including special offers,
                    discounts, and promotional updates at the phone number
                    provided. Frequency may vary. Message &amp; data rates may
                    apply. Reply STOP to unsubscribe at any time.
                  </span>
                </label>
              </div>

              <button type="submit" className="w-full bg-gold hover:bg-gold/90 text-white font-medium px-8 py-4 rounded-sm tracking-wide uppercase text-sm transition-all duration-300 hover:shadow-lg mt-4" data-id="element-68">
                Continue to Scheduling
              </button>
            </form>
          </>}
      </div>
    </div>;
}